import { useEffect, useState } from 'react';
import {
  formatTime,
  listPharmacistOnDutyGaps,
  type PharmacistOnDutyGap,
} from '@/lib/scheduling';

/**
 * Inline banner showing dates where no pharmacist covers the full operating
 * hours window (per SCH-AD-09 / v_pharmacist_on_duty_gaps).
 *
 * Pilot UX: collapsed summary by default, click to expand the per-date list.
 * Reads live from the view; never stored as state.
 */
export function PharmacistGapsBanner({
  locationId,
  monthStartDate,
}: {
  locationId: string;
  monthStartDate: string;
}) {
  const [gaps, setGaps] = useState<PharmacistOnDutyGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPharmacistOnDutyGaps(locationId, monthStartDate)
      .then((g) => {
        if (!cancelled) setGaps(g);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, monthStartDate]);

  if (loading) return null;
  if (error) return null;
  if (gaps.length === 0) {
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
        ✓ Pharmacist coverage looks complete for every open day this month.
      </div>
    );
  }

  // Group by date for the expanded list
  const byDate = new Map<string, PharmacistOnDutyGap[]>();
  for (const g of gaps) {
    if (!byDate.has(g.gap_date)) byDate.set(g.gap_date, []);
    byDate.get(g.gap_date)!.push(g);
  }
  const dateCount = byDate.size;

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 font-medium"
      >
        <span>⚠ Pharmacist-on-duty gaps</span>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs">
          {dateCount} {dateCount === 1 ? 'day' : 'days'}
        </span>
        <span className="text-xs text-amber-700">
          {open ? 'hide' : 'show details'}
        </span>
      </button>
      {open && (
        <ul className="space-y-1 text-xs pt-2 border-t border-amber-200">
          {Array.from(byDate.entries()).map(([date, dayGaps]) => (
            <li key={date} className="flex gap-3">
              <span className="font-mono w-28 shrink-0">{date}</span>
              <span>
                {dayGaps
                  .map((g) => `${formatTime(g.gap_start)}–${formatTime(g.gap_end)}`)
                  .join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
